import { Dialog, DialogModule } from '@angular/cdk/dialog'
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop'
import { DatePipe } from '@angular/common'
import { Component, DestroyRef, effect, inject } from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { COLORS } from 'src/app/config'
import { ITask, ITaskGroup, ITaskUpdate } from 'src/app/schemas'
import { IErrorRes } from 'src/app/schemas/base.schema'
import { TasksService } from 'src/app/services'

import { PopoverComponent } from '../../components/popover/popover.component'
import { UserPreviewComponent } from '../../components/user-preview/user-preview.component'
import { TaskComponent } from './task/task.component'

@Component({
  selector: 'app-tasks',
  imports: [
    FormsModule,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    DialogModule,
    DatePipe,
    UserPreviewComponent,
    ReactiveFormsModule,
    PopoverComponent,
  ],
  templateUrl: './tasks.component.html',
  styleUrl: './tasks.component.scss',
})
export class TasksComponent {
  private tasksService = inject(TasksService)
  private dialog = inject(Dialog)
  private formBuilder = inject(FormBuilder)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private destroyRef = inject(DestroyRef)

  createTaskForm = this.formBuilder.nonNullable.group({
    name: [''],
    groupId: [''],
  })

  groups = this.tasksService.groups

  // Modal and popover states
  groupOptionsActive: null | number = null
  groupColorsActive: null | number = null

  availableColors = Object.keys(COLORS)

  /**
   * Group ids to use in drag-drop
   */
  get groupLists() {
    return this.groups().map((g) => `group-${g.id}`)
  }

  constructor() {
    effect(() => {
      this.groups() // Rerun when groups changes

      const paramMapSub = this.route.paramMap.subscribe({
        next: (paramMap) => {
          const taskId = paramMap.get('taskId')
          if (taskId == null) return

          const task = this.tasksService.getTaskById(+taskId)
          if (task == null) return

          this.openDialog(task!)
        },
      })
      this.destroyRef.onDestroy(() => paramMapSub.unsubscribe())
    })
  }

  dropGroup(event: CdkDragDrop<ITaskGroup[]>) {
    const groups = event.container.data
    moveItemInArray(groups, event.previousIndex, event.currentIndex)

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i]
      this.tasksService.updateGroup(group.id, { order: i })
    }
  }

  dropTask(event: CdkDragDrop<ITaskGroup>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data.tasks,
        event.previousIndex,
        event.currentIndex,
      )

      // TODO: Bulk update all tasks in group
      const tasks = event.container.data.tasks

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i]
        this.tasksService.updateTask(task.id, { order: i })
      }
    } else {
      transferArrayItem(
        event.previousContainer.data.tasks,
        event.container.data.tasks,
        event.previousIndex,
        event.currentIndex,
      )
      const tasks = event.container.data.tasks

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i]

        const payload: Partial<ITaskUpdate> = { order: i }

        if (i === event.currentIndex) {
          payload['group'] = event.container.data.id
        }

        this.tasksService.updateTask(task.id, payload)
      }
    }
  }

  openDialog(task: ITask) {
    const dialogRef = this.dialog.open<
      { action: 'delete'; task: ITask },
      number
    >(TaskComponent, {
      minWidth: '300px',
      data: task.id,
    })

    dialogRef.closed.subscribe((result) => {
      this.router.navigateByUrl('/tasks')

      if (!result) return
      const { action, task } = result

      switch (action) {
        case 'delete':
          this.tasksService.deleteTask(task.id)
          break
        default:
          break
      }
    })
  }

  setGroupOptionsActive(groupId: number) {
    this.groupOptionsActive = groupId
  }

  unsetGroupOptionsActive(groupId: number) {
    if (this.groupOptionsActive === groupId) {
      this.groupOptionsActive = null
    }
  }

  onCreateGroup(formData: NgForm) {
    const name = formData.form.value.group

    this.tasksService.createGroup({ name }).subscribe({
      next: () => {
        formData.form.reset()
      },
      error: (errs: IErrorRes[]) => {
        formData.form.setErrors(errs.map((e) => e.detail))
      },
    })
  }

  onCreateTask(formData: NgForm, groupId: number) {
    const name = formData.form.value.task

    this.tasksService.createTask({ group: groupId, name }).subscribe({
      next: () => {
        formData.reset()
      },
      error: (res: IErrorRes[]) => {
        formData.form.setErrors(res.map((r) => r.detail))
      },
    })
  }

  onUpdateTask(id: number, payload: Partial<ITaskUpdate>) {
    this.tasksService.updateTaskDebounced(id, payload)
  }

  onDeleteTask(id: number) {
    this.tasksService.deleteTask(id)
  }

  onDeleteGroup(id: number) {
    this.tasksService.deleteGroup(id)
  }

  onUpdateGroup(id: number, name: string) {
    this.tasksService.updateGroupDebounced(id, { name })
  }

  toggleUpdateGroupColors(id: number, prev?: number | null) {
    if (prev === id) return // Short circuit to avoid race conditions

    if (this.groupColorsActive == null || this.groupColorsActive != id) {
      this.groupColorsActive = id
    } else {
      this.groupColorsActive = null
    }
  }

  handleGroupColorChange(groupId: number, color: string) {
    this.tasksService.updateGroupDebounced(groupId, { color })
  }
}
